import { ChevronDown, LogOut, Menu, UserRound } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Avatar } from "@/components/ui/Avatar";
import {
    HelpCenterButton,
    LanguageMenu,
    ThemeMenu,
} from "@/components/layout/HeaderControls";
import { findNavItem } from "@/components/layout/nav";
import { useDismiss } from "@/hooks/useDismiss";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/providers/AuthProvider";

interface HeaderProps {
    /** Opens the off-canvas sidebar; only rendered on small screens. */
    onOpenSidebar: () => void;
}

export function Header({ onOpenSidebar }: HeaderProps) {
    const { coach, logout } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const closeMenu = useCallback(() => setMenuOpen(false), []);
    const menuRef = useDismiss(menuOpen, closeMenu);

    const current = findNavItem(pathname);
    const displayName = coach?.displayName ?? coach?.coachName ?? "";

    const handleLogout = async () => {
        closeMenu();
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <header className="app-shell-panel app-shell-border sticky top-0 z-20 flex h-14 items-center justify-between gap-2 border-b px-3 sm:h-16 sm:px-4 lg:px-6">
            {/* Left: menu trigger + page title */}
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                <button
                    type="button"
                    onClick={onOpenSidebar}
                    aria-label={t("header.openMenu")}
                    className="app-shell-surface app-shell-border app-shell-text-muted inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border shadow-sm transition-colors hover:text-[var(--app-text-primary)] lg:hidden"
                >
                    <Menu className="h-5 w-5" />
                </button>

                <div className="min-w-0 flex-1">
                    <h1 className="app-text-primary truncate text-base font-semibold sm:text-lg lg:text-xl">
                        {current ? t(current.labelKey) : t("app.name")}
                    </h1>
                    {current && (
                        <p className="app-shell-text-muted hidden text-sm lg:block">
                            {t(current.subtitleKey)}
                        </p>
                    )}
                </div>
            </div>

            {/* Right: help center + theme + language + account menu */}
            <div className="flex shrink-0 items-center gap-1 sm:gap-1.5 lg:gap-4">
                <HelpCenterButton />
                <ThemeMenu />
                <LanguageMenu />

                {coach && (
                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            aria-label={t("header.userMenu")}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            className="app-shell-surface app-shell-border inline-flex h-9 w-9 items-center justify-center gap-2 rounded-full border shadow-sm transition-all lg:h-auto lg:w-auto lg:py-1.5 lg:pl-1.5 lg:pr-3"
                        >
                            <Avatar name={displayName} src={coach.avatarUrl} size="sm" />
                            <span className="hidden text-left lg:block">
                                <span className="app-text-primary block text-sm font-semibold leading-tight">
                                    {displayName}
                                </span>
                                <span className="app-text-soft block text-xs leading-tight">
                                    {coach.employmentType === "freelance"
                                        ? t("coach.freelance")
                                        : t("coach.inHouse")}
                                </span>
                            </span>
                            <ChevronDown
                                className={`app-text-soft hidden h-4 w-4 transition-transform lg:block ${
                                    menuOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        {menuOpen && (
                            <div
                                role="menu"
                                className="app-shell-panel app-shell-border absolute right-0 mt-2 w-56 overflow-hidden rounded-2xl border shadow-xl"
                            >
                                {/* Identity block: the pill hides it on mobile. */}
                                <div className="app-shell-border border-b px-4 py-3 lg:hidden">
                                    <p className="app-text-primary truncate text-sm font-semibold">
                                        {displayName}
                                    </p>
                                    <p className="app-text-soft truncate text-xs">
                                        {coach.email ?? coach.username}
                                    </p>
                                </div>

                                <Link
                                    to="/profile"
                                    role="menuitem"
                                    onClick={closeMenu}
                                    className="app-shell-text-muted app-shell-hover flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                                >
                                    <UserRound className="h-4 w-4" />
                                    {t("header.profile")}
                                </Link>

                                <div className="app-shell-border border-t pt-1">
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={handleLogout}
                                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-[var(--color-error-text)] transition-colors hover:bg-[var(--color-error-bg)]"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        {t("header.logout")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}
